// =====================================================
// services/auth/AuthService.js
// =====================================================
// Hauptservice für die Authentifizierung
import { getOrganizationName } from 'services/restApi';
import { logger } from './Logger';

export class AuthService {
  constructor(options = {}) {
    this.tokenService = options.tokenService;
    this.cognitoService = options.cognitoService;
    this.apolloService = options.apolloService;

    // Optionen für Batch-Prozesse
    this.batchOptions = {
      maxBatchSize: options.maxBatchSize || 10,
      batchTimeout: options.batchTimeout || 50 // ms
    };

    // Status
    this.isAuthenticated = false;
    this.isAnonymous = true;
    this.isLoading = false;
    this.userId = null;
    this.userGroups = [];
    this.userInfo = null;
    this.idTokenPayload = null;
    this.hasOrganizerAccess = false;
    this.organizations = [];

    // Request-Batching
    this.pendingApiBatches = {};

    // EventBus für Auth-Ereignisse
    this.eventListeners = {};

    // Track last notified state to prevent unnecessary re-renders
    this.lastNotifiedState = null;

    logger.debug('AuthService initialized');

    // Token-Refresh-Callback im TokenService registrieren
    if (this.tokenService) {
      this.tokenService.onTokenRefresh(this.refreshTokens.bind(this));
    }
  }

  // Auth-Status initialisieren
  async initialize() {
    try {
      logger.debug('Initializing auth service');
      this.isLoading = true;
      this.notifyStateChange();

      // Implementieren von Retry mit Backoff
      return await this.tokenService.retryWithBackoff(
        async () => {
          // Versuche, eine gültige Sitzung zu erhalten
          const session = await this.cognitoService.getSession();

          if (session) {
            // Auth-Daten aktualisieren
            await this.updateAuthData(session);

            const idToken = session.idToken.jwtToken;
            console.log('INITIAL COGNITO ID TOKEN:', idToken);

            logger.debug(
              'Auth initialization successful with authenticated user'
            );
            this.isLoading = false;
            this.notifyStateChange();
            return true;
          } else {
            // No session - anonymous user
            this.isAuthenticated = false;
            this.isAnonymous = true;
            this.isLoading = false;
            this.notifyStateChange();

            logger.debug('Auth initialization successful with anonymous user');
            return false;
          }
        },
        (error) => {
          // Nur bei Rate-Limit-Fehlern wiederholen
          return (
            error.name === 'TooManyRequestsException' ||
            (error.message && error.message.includes('rate'))
          );
        }
      );
    } catch (error) {
      logger.error('Error during auth initialization', error);

      // Fallback: anonymous user
      this.isAuthenticated = false;
      this.isAnonymous = true;

      this.isLoading = false;
      this.notifyStateChange();
      return false;
    }
  }

  // Auth-Daten basierend auf Session aktualisieren
  async updateAuthData(session) {
    try {
      logger.debug('Updating auth data from session');

      const payload = session.idToken.payload;
      const groups = payload['cognito:groups'] || [];

      this.userId = payload['custom:userId'] || null;
      this.userGroups = groups;
      this.userInfo = session.user;
      this.idTokenPayload = payload;
      this.hasOrganizerAccess = this.checkOrganizerAccess(groups);
      this.organizations = this.extractOrganizationIds(groups);
      this.isAuthenticated = true;
      this.isAnonymous = false;

      this.notifyStateChange();

      // Organisationsnamen im Hintergrund abrufen
      if (this.organizations.length > 0) {
        this.fetchOrganizationNames(this.organizations);
      }

      logger.debug('Auth data updated successfully');
      return true;
    } catch (error) {
      logger.error('Error updating auth data', error);
      return false;
    }
  }

  // Login mit Benutzername und Passwort
  async login(username, password) {
    try {
      logger.debug('Login initiated');
      this.isLoading = true;
      // this.notifyStateChange();
      // // Bei Cognito anmelden
      const authResult = await this.cognitoService
        .login(username, password)
        .catch((error) => {
          console.log('LOGIN AUTH ERROR:', error);
          throw error;
        });
      console.log('LOGIN AUTH RESULT:', authResult);
      // // Auth-Daten aktualisieren
      await this.updateAuthData(authResult);
      const idToken = authResult.idToken.jwtToken;
      console.log('LOGIN COGNITO ID TOKEN:', idToken);
      // Event für erfolgreichen Login auslösen
      this.notifyEvent('login:success', { userId: this.userId });
      this.isLoading = false;
      this.notifyStateChange();
      logger.debug('Login completed successfully');
      return authResult;
    } catch (error) {
      logger.error('Login error', error);

      // Event für fehlgeschlagenen Login auslösen
      this.notifyEvent('login:error', { error });

      // Loading-Status zurücksetzen
      this.isLoading = false;

      // State-Änderung notifizieren (wird nur gesendet wenn sich tatsächlich etwas geändert hat)
      this.notifyStateChange();

      throw error;
    }
  }

  // Tokens aktualisieren
  async refreshTokens(forceRefresh = false) {
    try {
      if (this.isAnonymous && !forceRefresh) {
        logger.debug('Skip token refresh for anonymous user');
        return false;
      }

      logger.debug('Refreshing tokens');

      // Prüfen, ob das Cognito-Refresh-Token gültig ist
      const isCognitoValid = await this.cognitoService.isRefreshTokenValid();

      if (!isCognitoValid) {
        logger.debug('Cognito refresh token invalid, cannot refresh tokens');
        return false;
      }

      // Cognito-Sitzung aktualisieren
      const session = await this.cognitoService.refreshIdToken();

      // Ausführliches Logging des aktualisierten ID-Tokens
      const idToken = session.idToken.jwtToken;
      logger.debug(
        `Cognito ID token refreshed: ${idToken.substring(0, 15)}...`
      );
      console.log('REFRESHED COGNITO ID TOKEN:', idToken);

      // Token-Format validieren
      if (!this.tokenService.isValidJwtFormat(session.idToken.jwtToken)) {
        logger.error('Received invalid format ID token from Cognito');
        return false;
      }

      // Token-Typ prüfen
      const tokenType = this.tokenService.getTokenType(
        session.idToken.jwtToken
      );
      if (tokenType !== 'cognito_id_token') {
        logger.error(`Unexpected token type from Cognito: ${tokenType}`);
        return false;
      }

      // Auth-Daten aktualisieren
      const success = await this.updateAuthData(session);
      logger.debug(`Auth data update success: ${success}`);

      if (success) {
        // Explizit den Apollo Client über den neuen Token informieren
        if (this.apolloService) {
          logger.debug('Notifying Apollo client about new tokens');
          this.notifyEvent('apollo:tokenRefreshed', {
            idToken: session.idToken.jwtToken
          });
        }

        logger.debug('Tokens refreshed successfully');
        return true;
      }

      logger.debug('Failed to update auth data with refreshed tokens');
      return false;
    } catch (error) {
      logger.error('Error refreshing tokens', error);

      // Prüfen auf abgelaufenes Refresh-Token
      if (
        error.toString().includes('Refresh Token has expired') ||
        error.toString().includes('Invalid refresh token')
      ) {
        logger.debug('Refresh token expired, logout required');
        // Event für abgelaufenes Token auslösen
        this.notifyEvent('token:expired', { error });
      }

      return false;
    }
  }

  // Sicherer Logout mit vollständiger Bereinigung
  async logout(redirectToLogin = false) {
    try {
      logger.debug('Performing secure logout');
      this.isLoading = true;
      this.notifyStateChange();

      // 1. Apollo-Client-Cache zurücksetzen (über Event)
      this.notifyEvent('apollo:resetCache');

      // 2. Cognito-Benutzer abmelden
      await this.cognitoService.logout();

      // 3. Token-Cache leeren
      if (this.tokenService) {
        this.tokenService.clearAllTokens();
      }

      // 4. Lokale Zustände zurücksetzen
      this.isAuthenticated = false;
      this.isAnonymous = true;
      this.userId = null;
      this.userGroups = [];
      this.userInfo = null;
      this.idTokenPayload = null;
      this.hasOrganizerAccess = false;
      this.organizations = [];

      // 5. Event für erfolgreichen Logout auslösen
      this.notifyEvent('logout:success');

      this.isLoading = false;
      this.notifyStateChange();

      // 6. Optional zur Login-Seite weiterleiten
      if (redirectToLogin) {
        logger.debug('Redirecting to login page');
        setTimeout(() => {
          window.location.href = '/login';
        }, 500);
      }

      logger.debug('Secure logout completed');
      return true;
    } catch (error) {
      logger.error('Error during logout', error);

      // Bei Fehlern trotzdem Status zurücksetzen
      this.isAuthenticated = false;
      this.isAnonymous = true;
      this.isLoading = false;
      this.notifyStateChange();

      // Bei Fehlern trotzdem zur Login-Seite weiterleiten
      if (redirectToLogin) {
        setTimeout(() => {
          window.location.href = '/login';
        }, 500);
      }

      return false;
    }
  }

  // Prüfen, ob der Benutzer Organisator-Zugriff hat
  checkOrganizerAccess(groups = []) {
    return groups.some(
      (group) =>
        group.startsWith('orga-admin-') || group.startsWith('event-admin-')
    );
  }

  // Organisations-IDs aus Benutzergruppen extrahieren
  extractOrganizationIds(groups = []) {
    return groups
      .filter((group) => group.startsWith('orga-admin-'))
      .map((group) => {
        const id = group.replace('orga-admin-', '');
        return { _id: id, name: '' }; // Initialisiere mit leeren Namen
      });
  }

  // Organisationsnamen abrufen (Batch-optimiert)
  async fetchOrganizationNames(orgs) {
    if (!orgs || orgs.length === 0) return;

    logger.debug(`Fetching names for ${orgs.length} organizations`);

    const batchGetOrgNames = this.createBatchFunction(
      async (orgBatch) => {
        try {
          const promises = orgBatch.map(async (org) => {
            try {
              const response = await getOrganizationName({
                organizationId: org._id
              });

              return {
                organizationId: org._id, // Match the working example's structure
                organizationName: response.organizationName
              };
            } catch (error) {
              logger.error(
                `Error fetching name for organization ${org._id}`,
                error
              );
              return null;
            }
          });

          const results = await Promise.all(promises);
          const successfulResults = results.filter(Boolean);

          // Update organizations using the correct property names
          if (successfulResults.length > 0) {
            this.organizations = this.organizations.map((org) => {
              const updated = successfulResults.find(
                (r) => r.organizationId === org._id
              );
              return updated
                ? {
                    _id: updated.organizationId,
                    name: updated.organizationName
                  }
                : org;
            });
            this.notifyStateChange();
          }

          return successfulResults;
        } catch (error) {
          logger.error('Error in organization name batch', error);
          throw error;
        }
      },
      this.batchOptions.maxBatchSize,
      this.batchOptions.batchTimeout
    );

    try {
      await Promise.all(orgs.map((org) => batchGetOrgNames(org)));
      logger.debug('Completed fetching organization names');
    } catch (error) {
      logger.error('Error fetching organization names', error);
    }
  }

  // API-Anfragen bündeln (Performance-Optimierung)
  createBatchFunction(processBatch, maxBatchSize = 10, timeout = 50) {
    const batches = {};
    let batchCounter = 0;

    return (item) => {
      return new Promise((resolve, reject) => {
        // Erstelle eine eindeutige Batch-ID
        const batchId = `batch_${Date.now()}_${batchCounter++}`;

        // Initialisiere den Batch, wenn er noch nicht existiert
        if (!batches[batchId]) {
          batches[batchId] = {
            items: [],
            callbacks: [],
            timeoutId: null
          };

          // Timeout-Funktion zum Verarbeiten des Batches
          batches[batchId].timeoutId = setTimeout(async () => {
            const { items, callbacks } = batches[batchId];
            delete batches[batchId];

            try {
              const results = await processBatch(items);

              // Ergebnisse an die entsprechenden Callbacks weitergeben
              if (Array.isArray(results)) {
                // Wenn die Ergebnisse als Array zurückgegeben werden
                items.forEach((item, index) => {
                  callbacks[index].resolve(results[index]);
                });
              } else {
                // Wenn die Ergebnisse als Objekt zurückgegeben werden
                callbacks.forEach((callback, index) => {
                  callback.resolve(results);
                });
              }
            } catch (error) {
              // Fehler an alle Callbacks weitergeben
              callbacks.forEach((callback) => {
                callback.reject(error);
              });
            }
          }, timeout);
        }

        // Item und Callbacks zum Batch hinzufügen
        batches[batchId].items.push(item);
        batches[batchId].callbacks.push({ resolve, reject });

        // Batch sofort verarbeiten, wenn maximale Größe erreicht ist
        if (batches[batchId].items.length >= maxBatchSize) {
          clearTimeout(batches[batchId].timeoutId);
          batches[batchId].timeoutId = setTimeout(() => {}, 0); // Sofort ausführen
        }
      });
    };
  }

  // Hilfsfunktion zum Abrufen eines Organisationsnamens
  // Diese Funktion muss an deine API angepasst werden
  // async getOrganizationName(organizationId) {
  //   // Beispiel-Implementierung

  //   return {
  //     organizationId,
  //     organizationName: `Organization ${organizationId}`
  //   };
  // }

  // Event Listener hinzufügen
  addEventListener(event, callback) {
    if (!this.eventListeners[event]) {
      this.eventListeners[event] = [];
    }

    this.eventListeners[event].push(callback);

    // Cleanup-Funktion zurückgeben
    return () => {
      this.eventListeners[event] = this.eventListeners[event].filter(
        (cb) => cb !== callback
      );
    };
  }

  // Event auslösen
  notifyEvent(event, data = {}) {
    if (this.eventListeners[event]) {
      this.eventListeners[event].forEach((callback) => {
        try {
          callback(data);
        } catch (error) {
          logger.error(`Error in event listener for ${event}`, error);
        }
      });
    }
  }

  // Status-Änderung auslösen
  notifyStateChange() {
    const newState = {
      isAuthenticated: this.isAuthenticated,
      isAnonymous: this.isAnonymous,
      isLoading: this.isLoading,
      userId: this.userId,
      userGroups: this.userGroups,
      hasOrganizerAccess: this.hasOrganizerAccess,
      organizations: this.organizations
    };

    // Prüfen, ob sich der State tatsächlich geändert hat
    if (
      !this.lastNotifiedState ||
      JSON.stringify(this.lastNotifiedState) !== JSON.stringify(newState)
    ) {
      this.lastNotifiedState = { ...newState };
      this.notifyEvent('state:change', newState);
    }
  }

  // WebView Authentication Processing
  async processWebViewAuthentication(sessionData) {
    try {
      console.log('📱 Processing WebView authentication...');
      logger.debug('Processing WebView authentication with data:', {
        hasIdToken: !!sessionData?.idToken,
        hasAccessToken: !!sessionData?.accessToken,
        hasRefreshToken: !!sessionData?.refreshToken,
        idTokenLength: sessionData?.idToken?.length,
        expiresAt: sessionData?.expiresAt
      });

      this.isLoading = true;
      this.notifyStateChange();

      // Comprehensive session data validation
      const validationResult = this.validateWebViewSessionData(sessionData);
      if (!validationResult.isValid) {
        throw new Error(
          `Invalid session data: ${validationResult.errors.join(', ')}`
        );
      }

      console.log('✅ Session data validation passed');

      // Decode and validate token payload
      const tokenPayload =
        sessionData.idTokenPayload ||
        this.decodeTokenPayload(sessionData.idToken);
      logger.debug('Token payload:', {
        sub: tokenPayload.sub,
        email: tokenPayload.email,
        username: tokenPayload['cognito:username'],
        groups: tokenPayload['cognito:groups'],
        exp: new Date(tokenPayload.exp * 1000).toISOString(),
        iat: new Date(tokenPayload.iat * 1000).toISOString()
      });

      // Check token expiration
      const now = Math.floor(Date.now() / 1000);
      if (tokenPayload.exp && tokenPayload.exp <= now) {
        throw new Error('Received token is already expired');
      }

      console.log('✅ Token expiration validation passed');

      // Validate token types
      const idTokenType = this.tokenService.getTokenType(sessionData.idToken);
      if (sessionData.accessToken) {
        const accessTokenType = this.tokenService.getTokenType(
          sessionData.accessToken
        );
        logger.debug('Token types:', { idTokenType, accessTokenType });
      }

      // Create a session-like object for compatibility with existing updateAuthData
      const mockSession = {
        idToken: {
          jwtToken: sessionData.idToken,
          payload: tokenPayload
        },
        accessToken: {
          jwtToken: sessionData.accessToken
        },
        refreshToken: {
          token: sessionData.refreshToken
        },
        user: tokenPayload
      };

      console.log('🔄 Updating auth data...');
      // Use existing updateAuthData method
      await this.updateAuthData(mockSession);

      // Post-authentication validation
      await this.validatePostAuthenticationState();

      // Event for successful WebView authentication
      this.notifyEvent('webview:login:success', {
        userId: this.userId,
        source: 'webview',
        userGroups: this.userGroups,
        hasOrganizerAccess: this.hasOrganizerAccess
      });

      this.isLoading = false;
      this.notifyStateChange();

      console.log('✅ WebView authentication completed successfully');
      logger.debug('Final auth state:', {
        isAuthenticated: this.isAuthenticated,
        userId: this.userId,
        userGroups: this.userGroups,
        hasOrganizerAccess: this.hasOrganizerAccess,
        organizationsCount: this.organizations.length
      });

      return true;
    } catch (error) {
      console.error('❌ WebView authentication failed:', error);
      logger.error('WebView authentication error', error);

      // Event for failed WebView authentication
      this.notifyEvent('webview:login:error', {
        error: error.message,
        source: 'webview',
        errorType: error.name,
        timestamp: new Date().toISOString()
      });

      this.isLoading = false;
      this.notifyStateChange();

      throw error;
    }
  }

  // Comprehensive session data validation
  validateWebViewSessionData(sessionData) {
    const errors = [];

    if (!sessionData) {
      errors.push('Session data is null or undefined');
      return { isValid: false, errors };
    }

    // Required fields
    if (!sessionData.idToken) {
      errors.push('Missing idToken');
    }

    if (!sessionData.accessToken) {
      errors.push('Missing accessToken');
    }

    // Validate token formats
    if (
      sessionData.idToken &&
      !this.tokenService.isValidJwtFormat(sessionData.idToken)
    ) {
      errors.push('Invalid idToken JWT format');
    }

    if (
      sessionData.accessToken &&
      !this.tokenService.isValidJwtFormat(sessionData.accessToken)
    ) {
      errors.push('Invalid accessToken JWT format');
    }

    // Validate expiration
    if (sessionData.expiresAt) {
      const expiresAt =
        typeof sessionData.expiresAt === 'string'
          ? parseInt(sessionData.expiresAt)
          : sessionData.expiresAt;

      if (expiresAt <= Date.now()) {
        errors.push('Session has already expired');
      }
    }

    return {
      isValid: errors.length === 0,
      errors
    };
  }

  // Post-authentication state validation
  async validatePostAuthenticationState() {
    try {
      console.log('🔍 Validating post-authentication state...');

      // Check authentication state
      if (!this.isAuthenticated) {
        throw new Error('Authentication state not updated correctly');
      }

      // Check user ID
      if (!this.userId) {
        logger.warn('User ID not set after authentication');
      }

      // Check tokens in TokenService
      const idToken = this.tokenService.getToken('idToken');
      if (!idToken) {
        throw new Error('ID token not stored in TokenService');
      }

      // Test API connectivity (if backend URI is available)
      if (process.env.REACT_APP_BACKEND_URI) {
        await this.testApiConnectivity();
      }

      console.log('✅ Post-authentication validation passed');
      return true;
    } catch (error) {
      console.warn('⚠️ Post-authentication validation failed:', error.message);
      logger.warn('Post-authentication validation failed', error);
      // Don't throw here, just log the warning
      return false;
    }
  }

  // Test API connectivity with current tokens
  async testApiConnectivity() {
    try {
      const idToken = this.tokenService.getToken('idToken');
      if (!idToken) {
        throw new Error('No ID token available for API test');
      }

      // Test a simple API endpoint (adjust URL as needed)
      const testUrl = `${process.env.REACT_APP_BACKEND_URI}/v1/user/profile`;

      const response = await fetch(testUrl, {
        method: 'GET',
        headers: {
          Authorization: `Bearer ${idToken}`,
          'Content-Type': 'application/json'
        }
      });

      console.log(
        `🌐 API connectivity test: ${response.status} ${response.statusText}`
      );

      if (response.status === 401) {
        throw new Error('API returned 401 - token not accepted by backend');
      }

      if (response.status >= 400) {
        logger.warn(`API test returned ${response.status}`, {
          url: testUrl,
          status: response.status,
          statusText: response.statusText
        });
      } else {
        console.log('✅ API connectivity test passed');
      }

      return response.ok;
    } catch (error) {
      console.warn('⚠️ API connectivity test failed:', error.message);
      logger.warn('API connectivity test failed', error);
      return false;
    }
  }

  // Helper method to decode token payload
  decodeTokenPayload(token) {
    try {
      const base64Payload = token.split('.')[1];
      const payload = JSON.parse(atob(base64Payload));
      return payload;
    } catch (error) {
      logger.error('Error decoding token payload', error);
      return {};
    }
  }
}
